/**
 * Application Routes Constants
 * 
 * Centralized route definitions to avoid hardcoding URLs throughout the application.
 * Use these constants whenever navigating or defining routes.
 * 
 * @example
 * // Instead of: this.router.navigate(['/auth/login'])
 * // Use: this.router.navigate([ROUTES.AUTH.LOGIN])
 */

export const ROUTES = {
  // Root Routes
  ROOT: '/',
  HOME: '/home',
  
  // Authentication Routes
  AUTH: {
    BASE: '/auth',
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    VERIFY_EMAIL: '/auth/verify-email',
    SPLASH: '/splash',
    OTP:'/auth/otp'
  },
  
  // Customer Routes
  CUSTOMER: {
    BASE: '/customer',
    DASHBOARD: '/customer/dashboard',
    PROFILE: '/customer/profile',
    LOANS: '/customer/loans',
    LOAN_DETAILS: '/customer/loans/:id',
    APPLY_LOAN: '/customer/apply-loan',
    PAYMENTS: '/customer/payments',
    PAYMENT_HISTORY: '/customer/payment-history',
    DOCUMENTS: '/customer/documents',
    NOTIFICATIONS: '/customer/notifications',
    SETTINGS: '/customer/settings',
    SUPPORT: '/customer/support'
  },
  
  // Admin Routes
  ADMIN: {
    BASE: '/admin',
    BRANCH_SELECTION: '/admin/branch-selection',
    DASHBOARD: '/admin/dashboard',
    USERS: '/admin/users',
    USER_DETAILS: '/admin/users/:id',
    LOANS: '/admin/loans',
    LOAN_DETAILS: '/admin/loans/:id',
    LOAN_APPLICATIONS: '/admin/loan-applications',
    REPORTS: '/admin/reports',
    SETTINGS: '/admin/settings',
    ANALYTICS: '/admin/analytics',
    NOTIFICATIONS: '/admin/notifications',
    AUDIT_LOGS: '/admin/audit-logs'
  },
  
  // Manager Routes
  MANAGER: {
    BASE: '/manager',
    DASHBOARD: '/manager/dashboard',
    TEAM: '/manager/team',
    TEAM_MEMBER: '/manager/team/:id',
    LOANS: '/manager/loans',
    LOAN_DETAILS: '/manager/loans/:id',
    APPLICATIONS: '/manager/applications',
    REPORTS: '/manager/reports',
    PERFORMANCE: '/manager/performance',
    APPROVALS: '/manager/approvals',
    NOTIFICATIONS: '/manager/notifications'
  },
  
  // Collection Agent Routes
  COLLECTION_AGENT: {
    BASE: '/collection-agent',
    DASHBOARD: '/collection-agent/dashboard',
    ASSIGNED_LOANS: '/collection-agent/assigned-loans',
    LOAN_DETAILS: '/collection-agent/loans/:id',
    FOLLOW_UPS: '/collection-agent/follow-ups',
    COLLECTION_HISTORY: '/collection-agent/collection-history',
    SCHEDULE: '/collection-agent/schedule',
    NOTIFICATIONS: '/collection-agent/notifications'
  },
  
  // Common Routes
  COMMON: {
    NOT_FOUND: '/not-found',
    UNAUTHORIZED: '/unauthorized',
    ERROR: '/error',
    MAINTENANCE: '/maintenance'
  }
} as const;

/**
 * Helper function to build dynamic routes with parameters
 * 
 * @example
 * buildRoute(ROUTES.CUSTOMER.LOAN_DETAILS, { id: '123' })
 * // Returns: '/customer/loans/123'
 */
export function buildRoute(route: string, params?: Record<string, string | number>): string {
  if (!params) return route;
  
  let builtRoute = route;
  Object.entries(params).forEach(([key, value]) => {
    builtRoute = builtRoute.replace(`:${key}`, String(value));
  });
  
  return builtRoute;
}

/**
 * Route Segments for use in route definitions
 */
export const ROUTE_SEGMENTS = {
  AUTH: 'auth',
  CUSTOMER: 'customer',
  ADMIN: 'admin',
  MANAGER: 'manager',
  COLLECTION_AGENT: 'collection-agent',
  LOGIN: 'login',
  REGISTER: 'register',
  FORGOT_PASSWORD: 'forgot-password',
  DASHBOARD: 'dashboard',
  LOANS: 'loans',
  APPLICATIONS: 'applications',
  PROFILE: 'profile',
  SETTINGS: 'settings',
  NOTIFICATIONS: 'notifications'
} as const;
